import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

export default function HoursTab() {
  const [settings, setSettings] = useState(null);
  const [stations, setStations] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [nb, setNb] = useState({ station_id: "", date: "", start_time: "10:00", end_time: "23:00", reason: "" });

  const load = () => {
    api.get("/admin/settings").then((r) => { setSettings(r.data); if (!nb.station_id) {} });
    api.get("/admin/stations").then((r) => { setStations(r.data); setNb((p) => ({ ...p, station_id: p.station_id || r.data[0]?.id || "" })); });
    api.get("/admin/blocks").then((r) => setBlocks(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveHours = async () => {
    try {
      await api.put("/admin/settings", { open_time: settings.open_time, close_time: settings.close_time, slot_interval: Number(settings.slot_interval), durations: settings.durations });
      toast.success("Hours & durations saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const setDur = (i, patch) => setSettings((p) => ({ ...p, durations: p.durations.map((d, idx) => idx === i ? { ...d, ...patch } : d) }));
  const addDur = () => setSettings((p) => ({ ...p, durations: [...p.durations, { minutes: 30, label: "New" }] }));
  const rmDur = (i) => setSettings((p) => ({ ...p, durations: p.durations.filter((_, idx) => idx !== i) }));

  const addBlock = async () => {
    try { await api.post("/admin/blocks", nb); toast.success("Block added"); setNb((p) => ({ ...p, reason: "" })); api.get("/admin/blocks").then((r) => setBlocks(r.data)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delBlock = async (id) => { await api.delete(`/admin/blocks/${id}`); api.get("/admin/blocks").then((r) => setBlocks(r.data)); };

  if (!settings) return <Loader2 className="animate-spin text-purple-400" />;
  const stName = (id) => stations.find((s) => s.id === id)?.name || id;

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-5">
        <h3 className="font-display font-bold mb-4">Operating Hours</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div><label className="text-xs text-purple-400 font-mono">Open Time</label><Input type="time" value={settings.open_time} onChange={(e) => setSettings({ ...settings, open_time: e.target.value })} data-testid="open-time" className="mt-1 h-11 bg-purple-950/30 border-purple-500/30" /></div>
          <div><label className="text-xs text-purple-400 font-mono">Close Time</label><Input type="time" value={settings.close_time} onChange={(e) => setSettings({ ...settings, close_time: e.target.value })} data-testid="close-time" className="mt-1 h-11 bg-purple-950/30 border-purple-500/30" /></div>
          <div><label className="text-xs text-purple-400 font-mono">Slot Interval (min)</label><Input type="number" value={settings.slot_interval} onChange={(e) => setSettings({ ...settings, slot_interval: e.target.value })} className="mt-1 h-11 bg-purple-950/30 border-purple-500/30" /></div>
        </div>
        <h3 className="font-display font-bold mt-6 mb-3">Durations</h3>
        <div className="space-y-2">
          {settings.durations.map((d, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input type="number" value={d.minutes} onChange={(e) => setDur(i, { minutes: Number(e.target.value) })} className="w-24 h-10 bg-purple-950/30 border-purple-500/30" />
              <Input value={d.label} onChange={(e) => setDur(i, { label: e.target.value })} className="flex-1 h-10 bg-purple-950/30 border-purple-500/30" />
              <Button size="icon" variant="outline" onClick={() => rmDur(i)} className="h-10 w-10 border-red-500/30 bg-transparent text-red-300"><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addDur} className="border-purple-500/30 bg-transparent mt-1"><Plus size={14} /> Add Duration</Button>
        </div>
        <Button onClick={saveHours} data-testid="save-hours" className="mt-5 bg-emerald-600 hover:bg-emerald-500"><Save size={16} /> Save Hours</Button>
      </div>

      <div className="glass rounded-2xl p-5">
        <h3 className="font-display font-bold mb-4">Block a Station (date & time)</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={nb.station_id} onChange={(e) => setNb({ ...nb, station_id: e.target.value })} className="h-11 rounded-xl bg-purple-950/40 border border-purple-500/30 px-3 text-sm">
            {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Input type="date" value={nb.date} onChange={(e) => setNb({ ...nb, date: e.target.value })} className="h-11 bg-purple-950/30 border-purple-500/30" />
          <Input type="time" value={nb.start_time} onChange={(e) => setNb({ ...nb, start_time: e.target.value })} className="h-11 bg-purple-950/30 border-purple-500/30" />
          <Input type="time" value={nb.end_time} onChange={(e) => setNb({ ...nb, end_time: e.target.value })} className="h-11 bg-purple-950/30 border-purple-500/30" />
        </div>
        <Input value={nb.reason} onChange={(e) => setNb({ ...nb, reason: e.target.value })} placeholder="Reason (optional)" className="mt-3 h-11 bg-purple-950/30 border-purple-500/30" />
        <Button onClick={addBlock} disabled={!nb.date} data-testid="add-block" className="mt-3 bg-purple-600 hover:bg-purple-500"><Ban size={16} /> Block Slot</Button>
        <div className="mt-4 space-y-2">
          {blocks.map((b) => (
            <div key={b.id} className="flex justify-between items-center text-sm glass rounded-xl px-3 py-2">
              <span>{stName(b.station_id)} · {b.date} · {b.start_time}–{b.end_time} {b.reason && `· ${b.reason}`}</span>
              <Button size="icon" variant="outline" onClick={() => delBlock(b.id)} className="h-8 w-8 border-red-500/30 bg-transparent text-red-300"><Trash2 size={13} /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
